import { ThemeConfig } from 'antd';
import { createStyles } from 'antd-style';
import colors from 'tailwindcss/colors';

export const useStyle = createStyles(({ prefixCls, css }) => ({
  linearGradientButton: css`
    &.${prefixCls}-btn-primary:not([disabled]):not(.${prefixCls}-btn-dangerous) {
      > span {
        position: relative;
      }

      &::before {
        content: '';
        background: linear-gradient(135deg, var(--color-app-secondary), var(--color-app-primary));
        position: absolute;
        inset: -1px;
        opacity: 1;
        transition: all 0.3s;
        border-radius: inherit;
      }

      &:hover::before {
        opacity: 0;
      }
    }
  `,
}));
const primary = {
  50: 'var(--color-app-primary-50)',
  100: 'var(--color-app-primary-100)',
  200: 'var(--color-app-primary-200)',
  300: 'var(--color-app-primary-300)',
  400: 'var(--color-app-primary-400)',
  500: 'var(--color-app-primary-500)', // base
  600: 'var(--color-app-primary-600)',
  700: 'var(--color-app-primary-700)',
  800: 'var(--color-app-primary-800)',
  900: 'var(--color-app-primary-900)',
  950: 'var(--color-app-primary-950)',
};
const secondary = {
  50: 'var(--color-app-secondary-50)',
  100: 'var(--color-app-secondary-100)',
  200: 'var(--color-app-secondary-200)',
  300: 'var(--color-app-secondary-300)',
  400: 'var(--color-app-secondary-400)',
  500: 'var(--color-app-secondary-500)', // base
  600: 'var(--color-app-secondary-600)',
  700: 'var(--color-app-secondary-700)',
  800: 'var(--color-app-secondary-800)',
  900: 'var(--color-app-secondary-900)',
  950: 'var(--color-app-secondary-950)',
};
export const theme = {
  primary: primary[500],
  secondary: secondary[500],
  primaryTint: primary,
  secondaryTint: secondary,
  icon: '#E0E0E0',
  border: '#E0E0E0',
  textPrimary: '#1f1f1f',
  textSecondary: '#7B7B7B',
  textHeading: '#333333',
  bgContainer: colors.gray[100], // Using Tailwind's gray[50] for light background
  bgWhite: '#ffffff',
  bgHover: '#f0f0f0',
  borderHeader: colors.gray[100],
  controlsHeight: 44,
};

const common = {
  colorBorder: theme.secondaryTint[100],
  hoverBorderColor: theme.primaryTint[300],
  activeBorderColor: theme.secondaryTint[300],
  controlHeight: theme.controlsHeight,
  colorBgContainer: 'white',
  colorPrimaryBorder: theme.secondaryTint[400],
  fontSize: 16,
  activeShadow: 'none',
};

export const appTheme: ThemeConfig = {
  token: {
    // fontSize: 13,
    colorLink: theme.primary,
    motion: false,
    // Seed Token
    colorIcon: 'var(--color-app-gray)', // global icon color
    colorText: 'var(--color-app-text-light)',
    colorPrimary: 'var(--color-app-primary)',
    colorPrimaryHover: 'var(--color-app-secondary-light)',
    // Alias Token
    // colorBgContainer: 'var(--color-app-gray)',
    // fontFamily: `'Inter', sans-serif`,
    fontFamily: `'Inter', sans-serif`,
  },
  components: {
    Table: {
      fontSize: 12,
      colorBgContainer: theme.bgWhite,
      colorBorderSecondary: 'var(--color-slate-50)',
      colorText: theme.textSecondary,
      colorTextHeading: theme.textHeading,
      headerBg: 'var(--color-slate-100)',
      rowHoverBg: 'var(--color-slate-100)',
      headerSplitColor: theme.borderHeader,
      paddingContentHorizontal: 0,
      padding: 0,
      cellPaddingInline: 0,
      borderRadius: 8,
    },

    Button: {
      colorPrimary: theme.primary,
      colorPrimaryHover: theme.primaryTint['600'],
      colorText: theme.textPrimary,
      textHoverBg: theme.primaryTint['400'],
      defaultHoverColor: theme.primary,
      primaryShadow: 'none',
    },

    DatePicker: {
      colorTextPlaceholder: 'var(--color-app-text-light)',
      colorText: 'var(--color-app-text)',
      cellHoverBg: 'var(--color-app-white)',
      padding: 0,
      cellActiveWithRangeBg: 'var(--color-app-gray-light)',
      controlItemBgActive: 'var(--color-app-primary)',
      colorTextHeading: 'var(--color-app-text-light)',
      ...common,
    },
    Layout: {
      bodyBg: 'var(--color-slate-50)',
    },
    Collapse: {
      // colorText: '#1f1f1f',
      // colorTextHeading: '#333333',
      colorBorder: '#E0E0E0',
      colorBgContainer: 'var(--color-app-gray-light)',
      headerPadding: '12px 16px',
      contentPadding: '12px 16px',
      headerBg: '#F5F5F5',
      contentBg: '#ffffff',
    },

    Calendar: {
      colorTextPlaceholder: 'var(--color-app-text-light)',
      controlItemBgHover: 'var(--color-app-gray)',
      colorText: 'var(--color-app-text)',
      padding: 0,
      ...common,
    },

    Input: {
      ...common,
    },
    InputNumber: {
      colorTextPlaceholder: 'var(--color-app-text-light)',
      colorText: 'var(--color-app-text)',
      handleHoverColor: 'var(--color-app-secondary-light)',
      handleBorderColor: 'var(--color-app-gray)',
      ...common,
    },
    Dropdown: {
      // colorBorderBg: 'red',
      // controlOutline: '#00b96b', // Shows a colored outline on hover/focus if supported
      colorBgContainer: 'var(--color-app-gray-light)',
      // colorText: '#7B7B7B',
      fontSize: 16,
    },
    Select: {
      colorTextPlaceholder: 'var(--color-app-text-light)',
      colorText: 'var(--color-app-text)',
      activeOutlineColor: 'none',
      optionSelectedBg: 'var(--color-slate-200)',
      optionSelectedFontWeight: 'normal',
      multipleItemBg: theme.secondaryTint[100],
      ...common,
    },

    Checkbox: {
      colorText: 'var(--color-app-text-light)',
      colorPrimary: '#3b82f6', // blue-500
      // colorPrimaryHover: '#3b82f6',
      colorBgContainerDisabled: theme.bgHover,
      colorBorder: '#d1d5db', // gray-300 - unchecked border
      colorPrimaryBorder: '#3b82f6', // blue-500
    },
    Badge: {
      colorWarningBg: 'var(--color-app-warning)',
    },
    Switch: {
      colorPrimary: 'var(--color-app-primary)',
    },

    Tabs: {
      itemSelectedColor: 'var(--color-app-text)',
      itemHoverColor: 'var(--color-app-primary)',
    },
    Tooltip: {
      colorBgSpotlight: 'var(--color-app-primary)',
      // colorText: 'red',
      sizePopupArrow: 0,
    },
    Pagination: {
      itemBg: 'var(--color-app-gray-light)',
      itemActiveBg: 'var(--color-app-primary)',
      colorPrimary: 'var(--color-app-white)', // For arrow/next/prev icons
      itemLinkBg: 'var(--color-app-gray-light)', // Background for prev/next buttons
    },
  },
};
